/** @jsxRuntime classic */
/** @jsx jsx */
import {jsx} from '@emotion/react';
import * as React from 'react';
import {FunctionComponent, useEffect, useState} from 'react';
import DropDown, {DropDownColor, DropDownOption, DropDownStyle} from '../DropDown';
import {CycleCountDetails, CycleCountReasonCode, REASON_CODES_BY_VALUE} from './CycleCountInterfaces';
import {calculateDeltaForCountedItem} from './helpers';

interface CompleteCycleCountConfirmationStepProps {
  cycleCount: CycleCountDetails;
  cycleCountReasonCodes: CycleCountReasonCode[];
  setDisableCompleteButton: (disable: boolean) => void;
  hasDiscrepancies: boolean;
}

const CompleteCycleCountConfirmationStep: FunctionComponent<CompleteCycleCountConfirmationStepProps> = ({
  cycleCount,
  cycleCountReasonCodes,
  setDisableCompleteButton,
  hasDiscrepancies
}) => {
  const [selectedReason, setSelectedReason] = useState<DropDownOption>();
  const [editNotes, setEditNotes] = useState<string>('');
  const [purchaseOrder, setPurchaseOrder] = useState<string>('');
  const [isPoRequired, setIsPoRequired] = useState<boolean>(false);
  const [reasonCodeButtonText, setReasonCodeButtonText] = useState<string>('+ Reason For Count');
  const [numberOfLocations, setNumberOfLocations] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(0);
  const [numberOfTotalUnits, setNumberOfTotalUnits] = useState<number>(0);

  const dropDownOptions: DropDownOption[] = cycleCountReasonCodes.map((code) => ({
    name: code.name,
    value: code.value
  }));

  useEffect(() => {
    calculateCountedNumbers();
  }, [cycleCount]);

  useEffect(() => {
    disableCompleteButton();
  }, [editNotes, purchaseOrder, selectedReason]);

  const calculateCountedNumbers = () => {
    const countedLocationIds: number[] = [];
    const countedItemIds: number[] = [];
    let totalUnitsCounted = 0;
    if (cycleCount) {
      for (const item of cycleCount.cycleCountItems) {
        countedItemIds.push(item?.inventory?.id);
        countedLocationIds.push(item?.location?.id);
        totalUnitsCounted += item?.countedQuantities.each || item?.countedQuantities.carton;
      }
    }
    setNumberOfLocations(Array.from(new Set(countedLocationIds)).filter(Number).length);
    setNumberOfItems(Array.from(new Set(countedItemIds)).filter(Number).length);
    setNumberOfTotalUnits(totalUnitsCounted || 0);
  };

  const reasonsThatRequirePO: string[] = ['vendor_damaged', 'vendor_overage', 'adjust_past_receipt'];

  const disableCompleteButton = () => {
    if (isPoRequired) {
      if (selectedReason?.value && editNotes && purchaseOrder) {
        setDisableCompleteButton(false);
        return;
      }
    }
    if (selectedReason?.value && editNotes) {
      setDisableCompleteButton(false);
      return;
    }
    setDisableCompleteButton(true);
    return;
  };

  const renderDetails = () => {
    return (
      <div style={{display: 'flex', flexDirection: 'column', marginTop: '20px'}}>
        DETAILS
        <div style={{marginTop: '5px', display: 'flex', flexDirection: 'column'}}>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <label>Locations Counted:</label>
            <div data-testid="locations-counted" style={{marginLeft: '5px'}}>
              {numberOfLocations}
            </div>
          </div>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <label>SKUs Counted:</label>
            <div data-testid="items-counted" style={{marginLeft: '5px'}}>
              {numberOfItems}
            </div>
          </div>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <label>Total Units Counted:</label>
            <div data-testid="total-units-counted" style={{marginLeft: '5px'}}>
              {numberOfTotalUnits}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotes = () => {
    return (
      <div
        style={{
          marginTop: '10px',
          marginBottom: '10px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        NOTES
        <textarea
          style={{minHeight: '200px', marginTop: '10px'}}
          name="complete-cycle-count-notes"
          onChange={handleEditNoteChange}
        />
      </div>
    );
  };

  const renderPo = () => {
    if (isPoRequired) {
      return (
        <div style={{marginTop: '20px', marginBottom: '5px'}}>
          <div style={{marginBottom: '5px'}}>PURCHASE ORDER NUMBER</div>
          <input type="text" className="form-control" aria-label="purchaseOrderId" onChange={handleEnterPo} />
        </div>
      );
    }
  };

  const renderDiscrepancies = () => {
    return (
      <div style={{display: 'flex', flexDirection: 'column', marginTop: '20px'}}>
        DISCREPANCIES
        <div style={{marginTop: '5px', display: 'flex', flexDirection: 'column'}}>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            {cycleCount.cycleCountItems.map((item, idx) => {
              const [showDelta, , , delta] = calculateDeltaForCountedItem(item);
              if (showDelta && Math.abs(delta) > 0) {
                return (
                  <div key={idx} style={{display: 'flex', flexDirection: 'row'}}>
                    <label>{item.inventory.sku}: </label>
                    <div
                      data-testid={'count-delta-' + idx}
                      style={{marginLeft: '5px', display: 'flex', flexDirection: 'row'}}
                    >
                      <div>{delta > 0 ? `+${delta}` : delta}</div>
                      <div className="newGrey4" style={{marginLeft: '5px'}}>
                        {item.reasonCode ? `(${REASON_CODES_BY_VALUE[item.reasonCode]?.name})` : ''}
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>
    );
  };

  const handleEditNoteChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditNotes(event.target.value);
  };

  const handleEnterPo = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPurchaseOrder(event.target.value);
  };

  const handleDropDownSelect = (reason: DropDownOption) => {
    setSelectedReason(reason);
    setIsPoRequired(reasonsThatRequirePO.includes(reason?.value as string));
    setReasonCodeButtonText('');
  };

  return (
    <React.Fragment>
      <p>
        On-hand inventory will be immediately updated to match your entries. Please verify that you are submitting
        accurate counts.
      </p>
      <div>
        <DropDown
          options={dropDownOptions}
          selected={selectedReason}
          style={DropDownStyle.modal}
          onSelect={handleDropDownSelect}
          prefixText={reasonCodeButtonText}
          color={DropDownColor.white}
        />
      </div>
      {renderPo()}
      {renderDetails()}
      {hasDiscrepancies && renderDiscrepancies()}
      {renderNotes()}
    </React.Fragment>
  );
};

export default CompleteCycleCountConfirmationStep;
